import { useState, useEffect, useCallback } from 'react';
import {
  Mail, Calendar, StickyNote, ArrowLeft, RefreshCw,
  Send, Clock, CheckCircle, XCircle, Plus, Search,
} from 'lucide-react';
import { useReplica } from '../contexts/ReplicaContext';
import { api } from '../lib/api';

type Tab = 'email' | 'calendar' | 'notes';

interface EmailDraft {
  id: string;
  to: string;
  subject: string;
  bodyPreview?: string;
  body?: string;
  status: 'draft' | 'approved' | 'sent' | 'failed';
  createdAt: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  attendees?: string[];
  location?: string;
}

interface Note {
  id: string;
  title: string;
  contentPreview: string;
  tags?: string[];
  createdAt: string;
}

interface AgentToolsPanelProps {
  replicaId: string;
  onBack: () => void;
}

export default function AgentToolsPanel({ replicaId, onBack }: AgentToolsPanelProps) {
  const { getReplicaById } = useReplica();
  const [activeTab, setActiveTab] = useState<Tab>('email');
  const [loading, setLoading] = useState(false);

  // Data states
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);

  // Calendar form
  const [showCalendarForm, setShowCalendarForm] = useState(false);
  const [calTitle, setCalTitle] = useState('');
  const [calStart, setCalStart] = useState('');
  const [calDuration, setCalDuration] = useState(60);

  // Notes form
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteTags, setNoteTags] = useState('');
  const [noteSearch, setNoteSearch] = useState('');

  const replica = getReplicaById(replicaId);
  const replicaName = replica?.name || 'Replica';

  // Fetch emails (Firestore email_drafts for this replica)
  const fetchDrafts = useCallback(async () => {
    // Email drafts are stored in Firestore and managed by the agent tool
    // For the UI we query directly from Firestore via client SDK
    // But since the backend tool stores them, we'll show a placeholder
    // until a dedicated API endpoint is added
    setDrafts([]);
  }, []);

  // Fetch calendar events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Calendar events are in Firestore collection 'calendar_events'
      // accessed via the agent tool executor. For the UI, show what's available.
      setEvents([]);
    } catch (err) {
      console.error('Failed to fetch calendar events:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch notes (uses training events API as proxy since notes use Prisma PersonalTask)
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      setNotes([]);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'email') fetchDrafts();
    if (activeTab === 'calendar') fetchEvents();
    if (activeTab === 'notes') fetchNotes();
  }, [activeTab, fetchDrafts, fetchEvents, fetchNotes]);

  const handleCreateCalendarEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!calTitle || !calStart) return;
    try {
      await api.post('/chat', {
        replicaId,
        message: `Create a calendar event titled "${calTitle}" starting at ${calStart} for ${calDuration} minutes.`,
        conversationHistory: [],
      });
      setShowCalendarForm(false);
      setCalTitle('');
      setCalStart('');
      setCalDuration(60);
    } catch (err) {
      console.error('Failed to create calendar event:', err);
    }
  };

  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteTitle || !noteContent) return;
    try {
      await api.post('/chat', {
        replicaId,
        message: `Create a note titled "${noteTitle}" with content: ${noteContent}${noteTags ? ` Tags: ${noteTags}` : ''}`,
        conversationHistory: [],
      });
      setShowNoteForm(false);
      setNoteTitle('');
      setNoteContent('');
      setNoteTags('');
    } catch (err) {
      console.error('Failed to create note:', err);
    }
  };

  const tabs: { id: Tab; label: string; icon: typeof Mail; count: number }[] = [
    { id: 'email', label: 'Email Drafts', icon: Mail, count: drafts.length },
    { id: 'calendar', label: 'Calendar', icon: Calendar, count: events.length },
    { id: 'notes', label: 'Notes', icon: StickyNote, count: notes.length },
  ];

  const statusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'text-green-500';
      case 'approved': return 'text-hig-accent';
      case 'failed': return 'text-hig-error';
      default: return 'text-hig-text-tertiary';
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircle size={14} />;
      case 'failed': return <XCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-xl transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-hig-text-primary">Agent Tools</h1>
          <p className="text-sm text-hig-text-secondary">
            View and manage actions taken by <span className="text-hig-accent">{replicaName}</span>
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-hig-bg-secondary rounded-xl">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id
                  ? 'bg-hig-bg-elevated text-hig-accent shadow-sm'
                  : 'text-hig-text-secondary hover:text-hig-text-primary'
                }`}
            >
              <Icon size={16} />
              {tab.label}
              {tab.count > 0 && (
                <span className="px-1.5 py-0.5 bg-hig-accent/10 text-hig-accent text-xs rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="card p-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={20} className="animate-spin text-hig-text-tertiary" />
          </div>
        )}

        {/* EMAIL TAB */}
        {activeTab === 'email' && !loading && (
          <div>
            {drafts.length === 0 ? (
              <div className="text-center py-12">
                <Mail size={36} className="mx-auto text-hig-text-tertiary mb-3" />
                <p className="text-hig-text-secondary font-medium">No email drafts yet</p>
                <p className="text-xs text-hig-text-tertiary mt-1">
                  When your Replica drafts emails, they'll appear here for approval
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {drafts.map((draft) => (
                  <div key={draft.id} className="flex items-start gap-4 p-4 bg-hig-bg-tertiary rounded-xl">
                    <div className={`mt-0.5 ${statusColor(draft.status)}`}>
                      {statusIcon(draft.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-hig-text-primary">{draft.subject}</p>
                        <span className={`text-xs capitalize ${statusColor(draft.status)}`}>{draft.status}</span>
                      </div>
                      <p className="text-xs text-hig-text-secondary mt-0.5">To: {draft.to}</p>
                      {draft.bodyPreview && (
                        <p className="text-xs text-hig-text-tertiary mt-1 line-clamp-2">{draft.bodyPreview}</p>
                      )}
                    </div>
                    {draft.status === 'draft' && (
                      <div className="flex gap-2">
                        <button className="px-3 py-1.5 bg-hig-accent text-white rounded-lg text-xs font-medium hover:bg-hig-accent/90">
                          Approve
                        </button>
                        <button className="px-3 py-1.5 text-hig-error border border-hig-error/30 rounded-lg text-xs font-medium hover:bg-hig-error/10">
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CALENDAR TAB */}
        {activeTab === 'calendar' && !loading && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-hig-text-secondary">Upcoming events managed by your Replica</p>
              <button
                onClick={() => setShowCalendarForm(!showCalendarForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-hig-accent hover:bg-hig-accent/10 rounded-lg transition-colors"
              >
                <Plus size={14} />
                Quick Event
              </button>
            </div>

            {showCalendarForm && (
              <form onSubmit={handleCreateCalendarEvent} className="mb-4 p-4 bg-hig-bg-tertiary rounded-xl space-y-3">
                <input
                  type="text"
                  value={calTitle}
                  onChange={(e) => setCalTitle(e.target.value)}
                  placeholder="Event title"
                  className="w-full px-3 py-2 bg-hig-bg-primary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                  required
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="datetime-local"
                    value={calStart}
                    onChange={(e) => setCalStart(e.target.value)}
                    className="px-3 py-2 bg-hig-bg-primary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                    required
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={calDuration}
                      onChange={(e) => setCalDuration(Number(e.target.value))}
                      min={15}
                      step={15}
                      className="w-20 px-3 py-2 bg-hig-bg-primary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                    />
                    <span className="text-xs text-hig-text-secondary">min</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowCalendarForm(false)} className="px-3 py-1.5 text-sm text-hig-text-secondary">Cancel</button>
                  <button type="submit" className="px-4 py-1.5 bg-hig-accent text-white rounded-lg text-sm font-medium hover:bg-hig-accent/90">
                    Ask Replica to Create
                  </button>
                </div>
              </form>
            )}

            {events.length === 0 ? (
              <div className="text-center py-12">
                <Calendar size={36} className="mx-auto text-hig-text-tertiary mb-3" />
                <p className="text-hig-text-secondary font-medium">No calendar events yet</p>
                <p className="text-xs text-hig-text-tertiary mt-1">
                  Your Replica can create and manage calendar events during conversations
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((event) => (
                  <div key={event.id} className="flex items-start gap-4 p-4 bg-hig-bg-tertiary rounded-xl">
                    <div className="w-12 text-center">
                      <p className="text-xs text-hig-text-tertiary uppercase">
                        {new Date(event.startTime).toLocaleDateString('en', { weekday: 'short' })}
                      </p>
                      <p className="text-lg font-bold text-hig-accent">
                        {new Date(event.startTime).getDate()}
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-hig-text-primary">{event.title}</p>
                      <p className="text-xs text-hig-text-secondary mt-0.5">
                        {new Date(event.startTime).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                        {' — '}
                        {new Date(event.endTime).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {event.location && (
                        <p className="text-xs text-hig-text-tertiary mt-0.5">{event.location}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* NOTES TAB */}
        {activeTab === 'notes' && !loading && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 flex-1 max-w-xs">
                <Search size={14} className="text-hig-text-tertiary" />
                <input
                  type="text"
                  value={noteSearch}
                  onChange={(e) => setNoteSearch(e.target.value)}
                  placeholder="Search notes..."
                  className="w-full px-2 py-1 bg-transparent border-b border-hig-divider text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                />
              </div>
              <button
                onClick={() => setShowNoteForm(!showNoteForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-hig-accent hover:bg-hig-accent/10 rounded-lg transition-colors"
              >
                <Plus size={14} />
                Quick Note
              </button>
            </div>

            {showNoteForm && (
              <form onSubmit={handleCreateNote} className="mb-4 p-4 bg-hig-bg-tertiary rounded-xl space-y-3">
                <input
                  type="text"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  placeholder="Note title"
                  className="w-full px-3 py-2 bg-hig-bg-primary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                  required
                />
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Note content..."
                  rows={3}
                  className="w-full px-3 py-2 bg-hig-bg-primary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent resize-none"
                  required
                />
                <input
                  type="text"
                  value={noteTags}
                  onChange={(e) => setNoteTags(e.target.value)}
                  placeholder="Tags (comma-separated)"
                  className="w-full px-3 py-2 bg-hig-bg-primary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowNoteForm(false)} className="px-3 py-1.5 text-sm text-hig-text-secondary">Cancel</button>
                  <button type="submit" className="px-4 py-1.5 bg-hig-accent text-white rounded-lg text-sm font-medium hover:bg-hig-accent/90">
                    Ask Replica to Save
                  </button>
                </div>
              </form>
            )}

            {notes.length === 0 ? (
              <div className="text-center py-12">
                <StickyNote size={36} className="mx-auto text-hig-text-tertiary mb-3" />
                <p className="text-hig-text-secondary font-medium">No notes yet</p>
                <p className="text-xs text-hig-text-tertiary mt-1">
                  Your Replica can create and search notes during conversations
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {notes
                  .filter(n => !noteSearch || n.title.toLowerCase().includes(noteSearch.toLowerCase()) || n.contentPreview.toLowerCase().includes(noteSearch.toLowerCase()))
                  .map((note) => (
                    <div key={note.id} className="p-4 bg-hig-bg-tertiary rounded-xl">
                      <p className="text-sm font-medium text-hig-text-primary">{note.title}</p>
                      <p className="text-xs text-hig-text-tertiary mt-1 line-clamp-3">{note.contentPreview}</p>
                      {note.tags && note.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {note.tags.map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 bg-hig-accent/10 text-hig-accent text-xs rounded">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
